import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoonStars, faShieldCheck, faClock, faTimer } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment-timezone';

import './PublicationCurfewModal.scss';
import Drawer from '../Drawer/Drawer';
import Button, { BUTTON_VARIANTS } from '../UI/Button/Button';
import Group from '../UI/Group/Group';
import createLink from '../../../libs/createLink';
import useCurrentUser from '../../../hooks/useCurrentUser';
import {
    fetchCertificationStatus,
    USER_CURRENT_CERTIFICATION_STATUS_KEY,
} from '../../../api/public-voissa/users';
import {
    formatCurfewTime,
    getPublicationCurfewConfig,
    publicationCurfewEndsAt,
} from '../../../libs/publicationCurfew';

// Modale du couvre-feu de publication (20h → 8h) : elle remplace, sur cette tranche horaire, la
// modale d'offres qui se serait ouverte pour une capability manquante. La sortie proposée est la
// certification — gratuite — et surtout pas un abonnement : payer ne serait pas une réponse
// honnête à « votre profil n'est pas identifié ».
//
// Enregistrée en modale GLOBALE (modalTypes, cf. src/index.js) pour deux raisons : elle est
// déclenchée depuis des endroits très différents (en-tête, feed, éditeurs) et le filet de sécurité
// (échec d'une mutation) doit pouvoir l'ouvrir sans connaître le composant appelant.

// `curfew` : bornes issues d'un refus 403 de l'API, quand la modale est ouverte par le filet
// global (cf. PublicationCurfewListener). Absent lors d'une ouverture au clic.
const PublicationCurfewModal = ({ handleClose = () => {}, curfew = null }) => {
    const navigate = useNavigate();
    const currentUser = useCurrentUser();

    // Une demande déjà déposée change tout le discours : inviter à « se certifier » quelqu'un dont
    // le dossier est en cours d'examen est le meilleur moyen de le faire recommencer pour rien.
    // La query est celle de la page certification (staleTime 5 min) : souvent déjà en cache.
    const { data: certificationStatus } = useQuery(
        [USER_CURRENT_CERTIFICATION_STATUS_KEY],
        () => fetchCertificationStatus(),
        { staleTime: 1000 * 60 * 5, enabled: !!currentUser?._id }
    );

    const isPending = certificationStatus?.['certification-video']?.statusText === 'pending';
    // Horaires affichés : jamais écrits en dur. Ils viennent du refus de l'API s'il y en a un,
    // sinon de `currentUser.publicationCurfew`, et à défaut du repli 20h → 8h. Régler la fenêtre
    // sur 20:30 côté serveur suffit donc, le texte suit.
    const config = getPublicationCurfewConfig(currentUser, curfew);
    const startLabel = formatCurfewTime(config.start);
    const endLabel = formatCurfewTime(config.end);
    // `endsAt` est déjà calculé par l'API quand elle refuse ; sinon on le déduit des bornes.
    const endsAt = config.endsAt ? new Date(config.endsAt) : publicationCurfewEndsAt(config);
    const endsAtLabel = endsAt ? moment(endsAt).tz(config.timezone).format('HH[h]mm') : endLabel;

    const goCertify = () => {
        handleClose();
        navigate(createLink('userMeSettingsCertification', currentUser));
    };

    return (
        <Drawer small open={true} closeBtn={false} handleClose={handleClose}>
            <div className="publication-curfew">
                <div className="publication-curfew-header">
                    <span className="publication-curfew-icon">
                        <FontAwesomeIcon icon={faMoonStars} />
                    </span>
                    <div className="publication-curfew-title">
                        La nuit, la publication est réservée aux profils certifiés
                    </div>
                </div>

                <p className="publication-curfew-text">
                    Entre {startLabel} et {endLabel}, seuls les membres dont le profil est certifié peuvent
                    publier sur Voissa. Notre équipe de modération étant moins présente la nuit, cette
                    mesure nous aide à limiter les abus et le spam sur cette tranche horaire.
                </p>

                {isPending ? (
                    <>
                        <div className="publication-curfew-highlight">
                            <FontAwesomeIcon icon={faTimer} className="publication-curfew-highlight-icon" />
                            <div>
                                <b>Votre demande de certification est en cours d'examen.</b> Dès qu'elle sera
                                validée, vous pourrez publier à toute heure.
                            </div>
                        </div>

                        <p className="publication-curfew-note">
                            <FontAwesomeIcon icon={faClock} /> En attendant, vous pourrez publier de nouveau
                            à partir de {endsAtLabel}.
                        </p>

                        <Group right>
                            <Button variant={BUTTON_VARIANTS.REVERSE} onClick={goCertify}>
                                Suivre ma demande
                            </Button>
                            <Button onClick={handleClose}>Fermer</Button>
                        </Group>
                    </>
                ) : (
                    <>
                        <div className="publication-curfew-highlight">
                            <FontAwesomeIcon icon={faShieldCheck} className="publication-curfew-highlight-icon" />
                            <div>
                                <b>La certification est gratuite</b> et prend moins de deux minutes&nbsp;: une
                                courte capture vidéo depuis votre navigateur suffit à confirmer que vous êtes
                                bien la personne de votre profil. Vos images ne sont jamais publiées.
                            </div>
                        </div>

                        <ul className="publication-curfew-benefits">
                            <li>Publiez à toute heure, sans restriction nocturne</li>
                            <li>Affichez le badge certifié sur votre profil</li>
                            <li>Rassurez les membres&nbsp;: un profil certifié inspire confiance</li>
                        </ul>

                        <p className="publication-curfew-note">
                            <FontAwesomeIcon icon={faClock} /> Sans certification, vous pourrez publier de
                            nouveau à partir de {endsAtLabel}.
                        </p>

                        <Group right>
                            <Button variant={BUTTON_VARIANTS.REVERSE} onClick={goCertify}>
                                Certifier mon profil
                            </Button>
                            <Button onClick={handleClose}>Plus tard</Button>
                        </Group>
                    </>
                )}
            </div>
        </Drawer>
    );
};

export default PublicationCurfewModal;
